import { usePermissions } from '@/hooks/usePermissions';
import { Lock, Construction, Mail } from 'lucide-react';

interface RestrictedContentProps {
  children: React.ReactNode;
  requiredPermissions: string | string[];
  fallbackTitle?: string;
  fallbackMessage?: string;
}

export function RestrictedContent({
  children,
  requiredPermissions,
  fallbackTitle = 'Módulo en Desarrollo',
  fallbackMessage = 'Este módulo está actualmente en desarrollo y solo está disponible para usuarios autorizados.',
}: RestrictedContentProps) {
  const { hasPermission } = usePermissions();

  const hasAccess = hasPermission(requiredPermissions);

  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6">
      <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header con gradiente */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-8 text-white">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-white/20 rounded-full animate-ping" />
              <div className="relative p-4 bg-white/10 rounded-full backdrop-blur-sm">
                <Construction className="w-12 h-12" />
              </div>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-center mb-2">{fallbackTitle}</h1>
          <p className="text-amber-100 text-center text-sm">
            Acceso restringido temporalmente
          </p>
        </div>

        {/* Contenido */}
        <div className="p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <Lock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Acceso Restringido
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                {fallbackMessage}
              </p>
            </div>
          </div>

          {/* Información adicional */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Construction className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              Estado del Módulo
            </h3>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 dark:text-amber-400 mt-0.5">•</span>
                <span>Este módulo está siendo desarrollado activamente</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 dark:text-amber-400 mt-0.5">•</span>
                <span>Solo usuarios con permisos especiales pueden acceder en esta fase</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 dark:text-amber-400 mt-0.5">•</span>
                <span>Estará disponible para todos los usuarios próximamente</span>
              </li>
            </ul>
          </div>

          {/* Contacto */}
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span>
              Si necesitas acceso, contacta al administrador del sistema
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
